/**
 * WlTenantPlanBindingCard — aba "Faturamento" do tenant no painel WL.
 *
 * REFATOR 2026-06-15:
 *   A seleção de plano agora reaproveita 100% o fluxo da tela /tenant/plans
 *   (orquestrador `tenant_checkout_create_order` via `wl_apply_tenant_plan`).
 *   O operador escolhe plano + periodicidade + meio de pagamento e o backend
 *   provisiona limites, libera funcionalidades, cria a assinatura/recorrência
 *   primária e a 1ª cobrança — exatamente como se o tenant tivesse contratado.
 *
 *   A "Recorrência primária" deixa de ser escolha manual: passa a ser sempre
 *   a assinatura criada/atualizada pelo orquestrador.
 *
 *   A seção "Recorrências adicionais" é preservada para manutenção manual.
 */
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2, Save, Package, Zap, Clock, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { InlineNotice } from "@/components/ui/inline-notice";
import { useToast } from "@/hooks/use-toast";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import {
  useWlEligiblePlansV1,
  useWlTenantPlanBinding,
  useWlTenantPlanBindingMutations,
} from "@/hooks/wl/useWlTenantPlanBinding";
import { useWlTenantBillingSummary, useWlTenantOverview } from "@/hooks/wl/useWlTenantDetails";
import { Input } from "@/components/ui/input";
import { useWlTenantMasterCustomer } from "@/hooks/wl/useWlTenantMasterCustomer";
import type { RecurrenceInterval } from "@/hooks/tenant/useTenantPlansV2";

interface Props {
  tenantId: string;
}

const fmtMoney = (v: number | null | undefined, m = "BRL") =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: m || "BRL" }).format(
    Number(v) || 0,
  );

export function WlTenantPlanBindingCard({ tenantId }: Props) {
  const { canEdit } = useWlPermissions();
  const canEditTenants = canEdit("wl.tenants");
  const { toast } = useToast();

  // Estado controlado dos selects
  const [planId, setPlanId] = useState<string | null>(null);
  const [recurrence, setRecurrence] = useState<RecurrenceInterval | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  // Diálogo de escolha de modo (somente quando já existe assinatura ativa)
  const [applyModeDialogOpen, setApplyModeDialogOpen] = useState(false);
  const [applyMode, setApplyMode] = useState<"immediate" | "await_payment">("await_payment");
  // Diálogo de prorrogação de trial (tenant-específico)
  const [extendTrialDialogOpen, setExtendTrialDialogOpen] = useState(false);
  // v1.1.0: seletor de data fixa (substitui input numérico de dias)
  const [extendTrialDate, setExtendTrialDate] = useState<Date | undefined>(undefined);

  const { data: binding, isLoading: bindingLoading } = useWlTenantPlanBinding(tenantId);
  const { data: summary } = useWlTenantBillingSummary(tenantId);
  const { data: overview } = useWlTenantOverview(tenantId);
  // Data máxima permitida para o trial: cadastro do tenant + 30 dias.
  // Fonte de verdade é o backend (RPC wl_extend_tenant_trial v1.1.0), mas
  // usamos aqui para desabilitar datas no calendário e validar antes de chamar.
  const maxAllowedTrialDate = useMemo(() => {
    if (!overview?.created_at) return null;
    const d = new Date(overview.created_at);
    d.setDate(d.getDate() + 30);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [overview?.created_at]);
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const { data: masterCustomer } = useWlTenantMasterCustomer(tenantId);
  const hasMasterCustomer = !!masterCustomer?.crm_customer_id;
  const { data: plans = [], isLoading: plansLoading } = useWlEligiblePlansV1(
    tenantId,
    hasMasterCustomer,
  );

  const { applyPlan, extendTrial } = useWlTenantPlanBindingMutations(tenantId);

  // Hidrata a seleção com o plano atual ao carregar o binding
  useEffect(() => {
    if (binding?.subscription_plan_id) {
      setPlanId(binding.subscription_plan_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binding?.binding_id]);

  const selectedPlan = plans.find((p) => p.plan_id === planId) ?? null;
  const availableRecurrences = selectedPlan?.recurrences ?? [];
  const availablePayments = selectedPlan?.accepted_payment_methods_resolved ?? [];

  // Limpa periodicidade/meio inválidos quando o plano muda
  useEffect(() => {
    if (recurrence && !availableRecurrences.some((r) => r.recurrence_interval === recurrence)) {
      setRecurrence(null);
    }
    if (paymentMethod && !availablePayments.some((p) => p.id === paymentMethod)) {
      setPaymentMethod(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const selectedRecurrence = availableRecurrences.find(
    (r) => r.recurrence_interval === recurrence,
  );

  // True quando o tenant JÁ tem assinatura ativa (caso troca de plano).
  // Backend roteia para tenant_subscription_apply_changes neste cenário e
  // respeita o apply_mode. Em 1ª contratação NÃO mostramos diálogo.
  const hasActiveSubscription = !!binding?.primary_assinatura_id;

  // Executa de fato a aplicação (chamado direto na 1ª contratação ou após
  // confirmação do diálogo de modo na troca).
  const executeApply = async (mode: "immediate" | "await_payment") => {
    if (!planId || !recurrence || !paymentMethod) return;
    try {
      const result = await applyPlan.mutateAsync({
        plan_id: planId,
        recurrence_interval: recurrence,
        payment_method: paymentMethod,
        addons: [],
        apply_mode: mode,
      });

      if (result?.requires_payment) {
        toast({
          title: "Mudança registrada — aguardando pagamento",
          description:
            "A troca de plano só será efetivada quando a cobrança vigente for paga.",
        });
      } else if (result?.change_applied_immediately) {
        toast({
          title: "Plano atualizado imediatamente",
          description:
            "As próximas cobranças usarão o novo valor. Limites e funcionalidades já refletem o plano.",
        });
      } else if (result?.change_scheduled) {
        toast({
          title: "Troca de plano agendada",
          description: result.effective_at
            ? `Será aplicada em ${new Date(result.effective_at).toLocaleString("pt-BR")}.`
            : "Será aplicada no próximo ciclo.",
        });
      } else {
        toast({
          title: "Plano contratado com sucesso",
          description: "Assinatura e 1ª cobrança criadas no master.",
        });
      }
    } catch (err: any) {
      toast({
        title: "Falha ao aplicar plano",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    }
  };

  // Clique no botão "Aplicar plano":
  //  - troca de plano (assinatura ativa) → abre diálogo de escolha
  //  - 1ª contratação                    → executa direto
  const handleApply = async () => {
    if (!planId || !recurrence || !paymentMethod) {
      toast({
        title: "Preencha plano, periodicidade e meio de pagamento",
        variant: "destructive",
      });
      return;
    }
    if (hasActiveSubscription) {
      setApplyMode("await_payment");
      setApplyModeDialogOpen(true);
      return;
    }
    await executeApply("await_payment");
  };

  const handleConfirmApplyMode = async () => {
    setApplyModeDialogOpen(false);
    await executeApply(applyMode);
  };

  // Confirma prorrogação de trial (tenant-específico) usando data fixa (v1.1.0)
  const handleConfirmExtendTrial = async () => {
    if (!extendTrialDate) {
      toast({ title: "Selecione uma data para o término do trial", variant: "destructive" });
      return;
    }
    // Constrói a data escolhida como fim-de-dia local (23:59:59.999)
    const target = new Date(extendTrialDate);
    target.setHours(23, 59, 59, 999);

    const now = new Date();
    if (target <= now) {
      toast({ title: "A data deve ser posterior a agora", variant: "destructive" });
      return;
    }
    if (maxAllowedTrialDate && target > maxAllowedTrialDate) {
      toast({
        title: "Data acima do limite permitido",
        description: `Máximo: ${format(maxAllowedTrialDate, "dd/MM/yyyy")} (30 dias após o cadastro do tenant).`,
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await extendTrial.mutateAsync({ newTrialEnd: target.toISOString() });
      toast({
        title: "Trial prorrogado",
        description: `Novo término: ${new Date(res.new_trial_end).toLocaleString("pt-BR")}`,
      });
      setExtendTrialDialogOpen(false);
    } catch (err: any) {
      toast({
        title: "Falha ao prorrogar trial",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    }
  };




  const isCurrentSelection =
    binding?.subscription_plan_id === planId &&
    !!planId &&
    !!recurrence &&
    !!paymentMethod;

  return (
    <Card className="rounded-xl border border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Package className="h-4 w-4 text-primary" />
          Plano contratado
        </CardTitle>
        <CardDescription className="text-xs">
          Aplicar um plano aqui executa o mesmo fluxo da contratação pelo tenant: provisiona
          limites, libera funcionalidades, cria a recorrência primária e a próxima cobrança no
          master.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasMasterCustomer && (
          <InlineNotice variant="warning">
            Vincule um cliente do tenant master no card acima para habilitar a seleção de plano.
          </InlineNotice>
        )}

        {/* ---------- Estado atual ---------- */}
        {binding?.binding_id && (
          <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Plano atual</span>
              <span className="font-medium">{binding.plan_name ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Recorrência primária</span>
              <span className="font-medium">
                {binding.primary_descricao ?? "—"}
                {binding.primary_valor != null && (
                  <>
                    {" — "}
                    {fmtMoney(binding.primary_valor, binding.primary_moeda ?? "BRL")}
                    {binding.primary_frequencia ? ` / ${binding.primary_frequencia}` : ""}
                  </>
                )}
              </span>
            </div>
            {binding.primary_status && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline" className="text-[10px]">
                  {binding.primary_status}
                </Badge>
              </div>
            )}
            {/* v1.1.0 (2026-07-21): Meio de pagamento e conta corrente
                resolvidos a partir da recorrência primária. */}
            {binding.primary_meio_pagamento && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Meio de pagamento</span>
                <span className="font-medium">{binding.primary_meio_pagamento}</span>
              </div>
            )}
            {binding.primary_conta_corrente && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Conta corrente</span>
                <span className="font-medium">{binding.primary_conta_corrente}</span>
              </div>
            )}
          </div>
        )}

        {/* ---------- Seleção (alterar / contratar) ---------- */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Plano</Label>
            <Select
              value={planId ?? ""}
              onValueChange={(v) => setPlanId(v || null)}
              disabled={!canEditTenants || !hasMasterCustomer || plansLoading || bindingLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={plansLoading ? "Carregando planos…" : "Selecione um plano"}
                />
              </SelectTrigger>
              <SelectContent>
                {plans.length === 0 ? (
                  <SelectItem value="__empty" disabled>
                    Nenhum plano elegível
                  </SelectItem>
                ) : (
                  plans.map((p) => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name}
                      {p.is_current ? " · atual" : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Periodicidade</Label>
            <Select
              value={recurrence ?? ""}
              onValueChange={(v) => setRecurrence((v || null) as RecurrenceInterval | null)}
              disabled={!canEditTenants || !planId || availableRecurrences.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !planId
                      ? "Selecione um plano"
                      : availableRecurrences.length === 0
                        ? "Sem recorrências"
                        : "Selecione a periodicidade"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableRecurrences.map((r) => (
                  <SelectItem key={r.recurrence_interval} value={r.recurrence_interval}>
                    {r.recurrence_interval} — {fmtMoney(r.price)}
                    {r.discount_percent ? ` (-${r.discount_percent}%)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Meio de pagamento</Label>
            <Select
              value={paymentMethod ?? ""}
              onValueChange={(v) => setPaymentMethod(v || null)}
              disabled={!canEditTenants || !planId || availablePayments.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !planId
                      ? "Selecione um plano"
                      : availablePayments.length === 0
                        ? "Sem meios aceitos"
                        : "Selecione o meio"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availablePayments.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.nome} · {m.tipo_pagamento}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedRecurrence && (
          <p className="text-[11px] text-muted-foreground">
            Valor recorrente: <strong>{fmtMoney(selectedRecurrence.price)}</strong>
            {selectedRecurrence.setup_fee
              ? ` · setup ${fmtMoney(selectedRecurrence.setup_fee)}`
              : ""}
            {isCurrentSelection
              ? " · este já é o plano atual — aplicar gerará atualização no master."
              : ""}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={handleApply}
            disabled={
              !canEditTenants ||
              !hasMasterCustomer ||
              !planId ||
              !recurrence ||
              !paymentMethod ||
              applyPlan.isPending
            }
          >
            {applyPlan.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Aplicar plano
          </Button>

          {/* Prorrogar trial — visível apenas quando o tenant possui trial (ativo ou expirado) */}
          {summary?.trial_ends_at && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setExtendTrialDate(undefined);
                setExtendTrialDialogOpen(true);
              }}
              disabled={!canEditTenants || extendTrial.isPending}
              title="Adiciona dias ao trial deste tenant específico (não altera o plano)"
            >
              <Clock className="mr-2 h-4 w-4" />
              Prorrogar trial
            </Button>
          )}
        </div>

        {/* v1.x (2026-08-18): seção "Recorrências adicionais" removida da UI
            (manutenção manual legada). As RPCs e o diálogo permanecem no
            projeto, apenas sem ponto de entrada nesta tela. */}
      </CardContent>



      {/* Diálogo de escolha do modo (somente em troca de plano de tenant com assinatura ativa) */}
      <AlertDialog open={applyModeDialogOpen} onOpenChange={setApplyModeDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Como aplicar a troca de plano?</AlertDialogTitle>
            <AlertDialogDescription>
              Existe uma assinatura ativa para este tenant. Escolha como a mudança deve ser aplicada.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Cards interativos: ícone em tile + radio customizado à direita */}
          <RadioGroup
            value={applyMode}
            onValueChange={(v) => setApplyMode(v as "immediate" | "await_payment")}
            className="gap-3"
          >
            {/* Opção 1: Aplicar imediatamente */}
            <label
              htmlFor="apply-mode-immediate"
              className={`group relative flex items-start gap-4 rounded-xl border-2 p-4 cursor-pointer transition-all ${
                applyMode === "immediate"
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-primary/40 hover:bg-muted/30"
              }`}
            >
              <RadioGroupItem value="immediate" id="apply-mode-immediate" className="sr-only" />
              <div
                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  applyMode === "immediate"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                }`}
              >
                <Zap className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    Aplicar imediatamente
                  </span>
                  <span
                    className={`flex-shrink-0 w-4 h-4 rounded-full transition-all ${
                      applyMode === "immediate"
                        ? "border-[5px] border-primary bg-background"
                        : "border border-border bg-background"
                    }`}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  Atualiza a assinatura e a cobrança vigente agora; pro-rata é calculado conforme
                  as configurações do módulo financeiro. Use quando a transição já foi negociada
                  ou paga por fora.
                </p>
              </div>
            </label>

            {/* Opção 2: Aguardar pagamento */}
            <label
              htmlFor="apply-mode-await"
              className={`group relative flex items-start gap-4 rounded-xl border-2 p-4 cursor-pointer transition-all ${
                applyMode === "await_payment"
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-primary/40 hover:bg-muted/30"
              }`}
            >
              <RadioGroupItem value="await_payment" id="apply-mode-await" className="sr-only" />
              <div
                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  applyMode === "await_payment"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                }`}
              >
                <Clock className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    Aguardar pagamento
                  </span>
                  <span
                    className={`flex-shrink-0 w-4 h-4 rounded-full transition-all ${
                      applyMode === "await_payment"
                        ? "border-[5px] border-primary bg-background"
                        : "border border-border bg-background"
                    }`}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  A mudança fica registrada e só será efetivada quando a cobrança vigente for paga.
                  Recomendado para o fluxo padrão pré-pago.
                </p>
              </div>
            </label>
          </RadioGroup>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={applyPlan.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmApplyMode} disabled={applyPlan.isPending}>
              {applyPlan.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de prorrogação de trial (tenant-específico) */}
      <AlertDialog open={extendTrialDialogOpen} onOpenChange={setExtendTrialDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              Prorrogar período de trial
            </AlertDialogTitle>
            <AlertDialogDescription>
              Escolhe uma nova data de término do trial <strong>somente deste tenant</strong>. Não
              altera a configuração padrão de dias de trial do plano.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 text-xs">
            <div className="rounded-md border bg-muted/30 p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Trial atual termina em</span>
                <span className="font-medium">
                  {summary?.trial_ends_at
                    ? new Date(summary.trial_ends_at).toLocaleString("pt-BR")
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cadastro do tenant</span>
                <span className="font-medium">
                  {overview?.created_at
                    ? new Date(overview.created_at).toLocaleDateString("pt-BR")
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Data máxima permitida</span>
                <span className="font-medium">
                  {maxAllowedTrialDate
                    ? format(maxAllowedTrialDate, "dd/MM/yyyy")
                    : "—"}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Nova data de término</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !extendTrialDate && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {extendTrialDate
                      ? format(extendTrialDate, "dd/MM/yyyy", { locale: ptBR })
                      : "Selecione uma data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    locale={ptBR}
                    selected={extendTrialDate}
                    onSelect={setExtendTrialDate}
                    disabled={(date) => {
                      if (date < tomorrow) return true;
                      if (maxAllowedTrialDate && date > maxAllowedTrialDate) return true;
                      return false;
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">
                Limite máximo: 30 dias após o cadastro do tenant (trava de segurança aplicada
                também no backend).
              </p>
            </div>
          </div>


          <AlertDialogFooter>
            <AlertDialogCancel disabled={extendTrial.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExtendTrial}
              disabled={extendTrial.isPending}
            >
              {extendTrial.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Clock className="mr-2 h-4 w-4" />
              )}
              Confirmar prorrogação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
